import { apiSlice } from "../store/api/adminApi"

// Robot SMS custom lists declaration (PDF). Exactly one version is active - organizers must accept it.
export type RobotSmsPolicyVersion = {
    id: number
    version: string
    documentUrl: string
    fileName: string | null
    fileSize: number | null
    isActive: boolean
    isDisabled: boolean
    uploadedByPhone: string | null
    activatedAt: string | null
    createdAt: string
    acceptedCount: number
}

export type RobotSmsDeclarationStatus = 'pending' | 'approved' | 'rejected' | 'revoked'

// "פרטי המפיק ואימות דיגיטלי" signed by an organizer. File links are signed and live 10 minutes.
export type RobotSmsDeclaration = {
    id: number
    policyVersion: string
    acceptedAt: string
    ip: string | null
    userAgent: string | null
    declarantFullName: string
    declarantIdNumber: string
    businessName: string
    businessRegistrationNumber: string
    dataSource: string
    email: string
    phone: string
    reviewStatus: RobotSmsDeclarationStatus
    reviewComment: string | null
    reviewedAt: string | null
    reviewedByPhone: string | null
    idPhotoType: string | null
    idPhotoUrl: string | null
    signatureUrl: string | null
    user: { id: number, name: string | null, surname: string | null, phone: string | null, email: string | null } | null
}

export type RobotSmsDeclarationsQuery = { skip: number, take: number, search?: string, status?: RobotSmsDeclarationStatus }

export type RobotSmsReviewAction = 'approve' | 'reject' | 'revoke'

export type RobotSmsSettings = { autoApproveDeclarations: boolean, updatedAt: string, updatedByPhone: string | null }

export const robotSmsPolicyApi = apiSlice.enhanceEndpoints({ addTagTypes: ['RobotSmsPolicy', 'RobotSmsDeclarations', 'RobotSmsSettings'] }).injectEndpoints({
    endpoints: (build) => ({
        getRobotSmsPolicyVersions: build.query<RobotSmsPolicyVersion[], void>({
            query: () => ({ url: `/admin/robot-sms/policy/versions` }),
            providesTags: ['RobotSmsPolicy'],
        }),
        // FormData: `file` (PDF) + `version`. The uploaded version becomes the active one.
        uploadRobotSmsPolicyVersion: build.mutation<RobotSmsPolicyVersion, FormData>({
            query: (body) => ({
                url: `/admin/robot-sms/policy/versions`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['RobotSmsPolicy'],
        }),
        activateRobotSmsPolicyVersion: build.mutation<RobotSmsPolicyVersion, number>({
            query: (id) => ({
                url: `/admin/robot-sms/policy/versions/${id}/activate`,
                method: 'PUT',
            }),
            invalidatesTags: ['RobotSmsPolicy'],
        }),
        setRobotSmsPolicyVersionDisabled: build.mutation<RobotSmsPolicyVersion, { id: number, isDisabled: boolean }>({
            query: ({ id, isDisabled }) => ({
                url: `/admin/robot-sms/policy/versions/${id}/disabled`,
                method: 'PUT',
                body: { isDisabled },
            }),
            invalidatesTags: ['RobotSmsPolicy'],
        }),
        getRobotSmsDeclarations: build.query<{ list: RobotSmsDeclaration[], count: number, pendingCount: number }, RobotSmsDeclarationsQuery>({
            query: ({ skip, take, search, status }) => ({
                url: `/admin/robot-sms/policy/declarations`,
                params: { skip, take, ...(search ? { search } : {}), ...(status ? { status } : {}) },
            }),
            providesTags: ['RobotSmsDeclarations'],
        }),
        // The organizer gets a notification about the decision
        reviewRobotSmsDeclaration: build.mutation<unknown, { id: number, action: RobotSmsReviewAction, comment?: string }>({
            query: ({ id, action, comment }) => ({
                url: `/admin/robot-sms/policy/declarations/${id}/review`,
                method: 'PUT',
                body: { action, ...(comment ? { comment } : {}) },
            }),
            invalidatesTags: ['RobotSmsDeclarations', 'RobotSmsPolicy'],
        }),
        getRobotSmsSettings: build.query<RobotSmsSettings, void>({
            query: () => ({ url: `/admin/robot-sms/policy/settings` }),
            providesTags: ['RobotSmsSettings'],
        }),
        updateRobotSmsSettings: build.mutation<RobotSmsSettings, { autoApproveDeclarations: boolean }>({
            query: (body) => ({
                url: `/admin/robot-sms/policy/settings`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['RobotSmsSettings'],
        }),
    })
})

// Nest error body: { message: string | string[] }
export const getApiErrorMessage = (error: unknown): string | undefined => {
    const message = (error as { data?: { message?: string | string[] } })?.data?.message

    return Array.isArray(message) ? message[0] : message
}
